import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Collapse } from 'react-collapse';
import { withStyles } from 'theme/withStyles';
import Field from 'containers/Field';
import dataActions from 'core/data/actions';
import Checkmark from './components/Checkmark';
import Subtext from './components/Subtext';
import Content from './components/Content';
import DetailHandle from './components/DetailHandle';

import commonStyles from './styles';
import DisplayRateAmount from './components/DisplayRateAmount';
import Label from './components/Label';

const styles = (theme) => {
  const common = {
    ...commonStyles(theme),
  };
  return {
    ...common,
    amounts: {
      [`@media(max-width: ${theme.screenSizes.mobileMax}px)`]: {
        '& div': {
          marginTop: 20,
        },
        '& div:first-child': {
          marginTop: 0,
        },
        flexDirection: 'column',
      },
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: 27,
      width: '100%',
    },
    rate: {
      paddingTop: 15,
    },
    stats: {
      ...common.stats,
      '& div': {
        minWidth: undefined,
      },
    },
  };
};

const getMonthlyPaymentAmount = ({ amount: a, apr, months: n }) => {
  const r = parseFloat(apr) / 12 / 100;
  return a && n ? a && (a * (r * (1 + r) ** n)) / ((1 + r) ** n - 1) : 0;
};

export const CeLoan = ({
  amount,
  changeData,
  classes,
  fieldAmount,
  fieldChosen,
  fieldMonthlyRepaymentAmount,
  fieldTerm,
  fieldTotalInterestPaid,
  interestRate,
  isInAllBundle,
  isOfferSelected,
  labelMonthlyRepaymentAmount,
  labelRate,
  labelTotalInterestPaid,
  months,
  name,
  offerLabelText,
  rates,
  showOffersDetails,
  subText,
}) => {
  const [collapsed, setCollapse] = useState(!showOffersDetails);
  const monthlyPaymentAmount = getMonthlyPaymentAmount({ amount, apr: interestRate, months });
  const totalInterest = months && monthlyPaymentAmount ? months * monthlyPaymentAmount - amount : 0;
  const hasInterestRate = interestRate !== undefined && interestRate !== null;

  useEffect(() => {
    changeData(fieldMonthlyRepaymentAmount, monthlyPaymentAmount);
    changeData(fieldTotalInterestPaid, totalInterest);
  }, [
    changeData,
    fieldMonthlyRepaymentAmount,
    fieldTotalInterestPaid,
    monthlyPaymentAmount,
    totalInterest,
  ]);
  return (
    <>
      {!isInAllBundle ? <Checkmark name={name} /> : <Label label={offerLabelText} />}
      <DetailHandle setCollapse={setCollapse} collapsed={collapsed} />
      <Collapse isOpened={!collapsed}>
        <div className="content">
          <Content fieldChosen={fieldChosen} collapsed={collapsed}>
            <Subtext>{subText}</Subtext>
            {rates && <Field fieldName={rates} groupName={name} />}
            <div className={classnames('stats', classes.stats)}>
              {fieldTerm && (
                <div className={classnames('region-field-amount', classes.amount)}>
                  <Field
                    fieldName={fieldAmount}
                    groupName={name}
                    shouldValidate={isOfferSelected}
                  />
                </div>
              )}
              {fieldTerm && (
                <div className={classnames('region-field-term', classes.amount)}>
                  <Field fieldName={fieldTerm} groupName={name} />
                </div>
              )}
              {hasInterestRate && (
                <div className={classnames('amounts', classes.amounts)}>
                  <DisplayRateAmount label={labelRate} value={interestRate} isPercentage />
                  <DisplayRateAmount
                    label={labelMonthlyRepaymentAmount}
                    value={monthlyPaymentAmount.toLocaleString('en-US', {
                      currency: 'USD',
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                      style: 'currency',
                    })}
                    className="mcra"
                  />
                  <DisplayRateAmount
                    label={labelTotalInterestPaid}
                    value={totalInterest.toLocaleString('en-US', {
                      currency: 'USD',
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                      style: 'currency',
                    })}
                  />
                </div>
              )}
            </div>
          </Content>
        </div>
      </Collapse>
    </>
  );
};

CeLoan.propTypes = {
  amount: PropTypes.number,
  changeData: PropTypes.func.isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  fieldAmount: PropTypes.string.isRequired,
  fieldChosen: PropTypes.string.isRequired,
  fieldMonthlyRepaymentAmount: PropTypes.string.isRequired,
  fieldTerm: PropTypes.string.isRequired,
  fieldTotalInterestPaid: PropTypes.string.isRequired,
  interestRate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isInAllBundle: PropTypes.bool,
  isOfferSelected: PropTypes.bool.isRequired,
  labelMonthlyRepaymentAmount: PropTypes.string.isRequired,
  labelRate: PropTypes.string.isRequired,
  labelTotalInterestPaid: PropTypes.string.isRequired,
  months: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  offerLabelText: PropTypes.string,
  rates: PropTypes.string,
  showOffersDetails: PropTypes.bool.isRequired,
  subText: PropTypes.string,
};

CeLoan.defaultProps = {
  amount: 0,
  interestRate: undefined,
  isInAllBundle: false,
  offerLabelText: '',
  rates: '',
  subText: '',
};

const CeLoanBase = withStyles(styles)(CeLoan);

const mapDispatchToProps = ({ configs, data, fields, offers }, { name }) => {
  const offer = offers[name];
  const labelName = fields[offer.fieldTerm]?.lookupValues[data[offer.fieldTerm]];
  const monthMultiplier = /year/gi.test(labelName) ? 12 : 1;
  const dayMultiplier = /day/gi.test(labelName) ? 12 / 365 : 1;
  const interestRate = offer.termRateMap?.[data[offer.fieldTerm]];
  return {
    amount:
      typeof data[offer.fieldAmount] === 'string'
        ? parseFloat(data[offer.fieldAmount].replace(/,/g, ''))
        : data[offer.fieldAmount],
    fieldAmount: offer.fieldAmount,
    fieldChosen: offer.fieldChosen,
    fieldMonthlyRepaymentAmount: offer.fieldMonthlyRepaymentAmount,
    fieldTerm: offer.fieldTerm,
    fieldTotalInterestPaid: offer.fieldTotalInterestPaid,
    interestRate,
    isOfferSelected: data[offer.fieldChosen],
    labelMonthlyRepaymentAmount: offer.labelMonthlyRepaymentAmount,
    labelRate: offer.labelRate,
    labelTotalInterestPaid: offer.labelTotalInterestPaid,
    months: parseFloat(data[offer.fieldTerm]) * monthMultiplier * dayMultiplier || 0,
    offerLabelText: offer.label,
    rates: offer.rates,
    showOffersDetails: configs.showOffersDetails,
    subText: offer.subText,
  };
};

export default connect(mapDispatchToProps, { changeData: dataActions.changeData })(CeLoanBase);
